import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { vehicleApi } from '../services/api';
import { VehicleSearchParams } from '../types/api';
import VehicleCard from '../components/VehicleCard';
import LoadingSpinner from '../components/LoadingSpinner';
import SearchFilters, { MobileFilterDrawer } from '../components/SearchFilters';
import Pagination from '../components/Pagination';
import { 
  FunnelIcon, 
  Cog6ToothIcon, 
  TruckIcon,
  AdjustmentsHorizontalIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const VehicleListingPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [searchFilters, setSearchFilters] = useState<VehicleSearchParams>({
    page: 1,
    page_size: 20,
    sort_by: 'auction_date',
    sort_order: 'desc'
  });

  // Initialize filters from URL params
  useEffect(() => {
    try {
      const filters: VehicleSearchParams = {
        keyword: searchParams.get('keyword') || undefined,
        make: searchParams.get('make') || undefined,
        model: searchParams.get('model') || undefined,
        year_from: searchParams.get('year_from') ? parseInt(searchParams.get('year_from')!) : undefined,
        year_to: searchParams.get('year_to') ? parseInt(searchParams.get('year_to')!) : undefined,
        location: searchParams.get('location') || undefined,
        province: searchParams.get('province') || undefined,
        country: searchParams.get('country') || undefined,
        source: searchParams.get('source') || undefined,
        auction_status: searchParams.get('auction_status') || undefined,
        damage_type: searchParams.get('damage_type') || undefined,
        min_bid: searchParams.get('min_bid') ? parseFloat(searchParams.get('min_bid')!) : undefined,
        max_bid: searchParams.get('max_bid') ? parseFloat(searchParams.get('max_bid')!) : undefined,
        min_odometer: searchParams.get('min_odometer') ? parseInt(searchParams.get('min_odometer')!) : undefined,
        max_odometer: searchParams.get('max_odometer') ? parseInt(searchParams.get('max_odometer')!) : undefined,
        is_buy_now: searchParams.get('is_buy_now') === 'true' ? true : undefined,
        is_featured: searchParams.get('is_featured') === 'true' ? true : undefined,
        is_luxury: searchParams.get('is_luxury') === 'true' ? true : undefined,
        page: searchParams.get('page') ? parseInt(searchParams.get('page')!) : 1,
        page_size: searchParams.get('page_size') ? parseInt(searchParams.get('page_size')!) : 20,
        sort_by: searchParams.get('sort_by') || 'auction_date',
        sort_order: (searchParams.get('sort_order') as 'asc' | 'desc') || 'desc'
      };

      setSearchFilters(filters);
    } catch (error) {
      console.error('Error parsing URL params:', error);
    }
  }, [searchParams]);

  // Fetch vehicles
  const { data: vehiclesData, isLoading, error } = useQuery(
    ['vehicles', searchFilters],
    () => vehicleApi.searchVehicles(searchFilters),
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
      retry: 2,
      onError: (error) => {
        console.error('Error fetching vehicles:', error);
      }
    }
  );

  // Fetch filter options
  const { data: filterOptions } = useQuery(
    'vehicle-filters',
    vehicleApi.getSearchFilters,
    {
      staleTime: 10 * 60 * 1000,
      retry: 2,
      onError: (error) => {
        console.error('Error fetching filter options:', error);
      }
    }
  );

  const handleFilterChange = (newFilters: VehicleSearchParams) => {
    try {
      const updatedFilters = { ...newFilters, page: 1 };
      setSearchFilters(updatedFilters);

      const params = new URLSearchParams();
      Object.entries(updatedFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.set(key, String(value));
        }
      });
      setSearchParams(params);
    } catch (error) {
      console.error('Filter change error:', error);
    }
  };

  const handlePageChange = (page: number) => {
    try {
      const updatedFilters = { ...searchFilters, page };
      setSearchFilters(updatedFilters);
      
      const params = new URLSearchParams();
      Object.entries(updatedFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.set(key, value.toString());
        }
      });
      setSearchParams(params);
      
      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Page change error:', error);
    }
  };

  const handleSortChange = (sortBy: string, sortOrder: 'asc' | 'desc') => {
    handleFilterChange({ ...searchFilters, sort_by: sortBy, sort_order: sortOrder });
  };

  const clearFilters = () => {
    const basicFilters: VehicleSearchParams = {
      page: 1,
      page_size: 20,
      sort_by: 'auction_date',
      sort_order: 'desc'
    };
    setSearchFilters(basicFilters);
    setSearchParams(new URLSearchParams());
  };

  const hasActiveFilters = Object.entries(searchFilters).some(([key, value]) => {
    if (['page', 'page_size', 'sort_by', 'sort_order'].includes(key)) return false;
    return value !== undefined && value !== null && value !== '';
  });

  const activeFilterCount = Object.entries(searchFilters).filter(([key, value]) => {
    if (['page', 'page_size', 'sort_by', 'sort_order'].includes(key)) return false;
    return value !== undefined && value !== null && value !== '';
  }).length;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Аукциони</h1>
              <p className="text-sm sm:text-base text-gray-600 mt-1">
                {vehiclesData ? (
                  <span className="font-medium">{vehiclesData.total.toLocaleString()}</span>
                ) : (
                  '...'
                )} автомобила
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Sort - Desktop */}
              <div className="hidden sm:block">
                <select
                  value={`${searchFilters.sort_by}-${searchFilters.sort_order}`}
                  onChange={(e) => {
                    const [sortBy, sortOrder] = e.target.value.split('-');
                    handleSortChange(sortBy, sortOrder as 'asc' | 'desc');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="auction_date-desc">Търг (Най-нови)</option>
                  <option value="auction_date-asc">Търг (Най-стари)</option>
                  <option value="year-desc">Година (Най-нови)</option>
                  <option value="year-asc">Година (Най-стари)</option>
                  <option value="make-asc">Марка (A-Z)</option>
                  <option value="make-desc">Марка (Z-A)</option>
                  <option value="current_bid-desc">Цена (Най-висока)</option>
                  <option value="current_bid-asc">Цена (Най-ниска)</option>
                </select>
              </div>

              {/* Mobile Filter Button */}
              <button
                onClick={() => setShowMobileFilters(true)}
                className="lg:hidden flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
              >
                <AdjustmentsHorizontalIcon className="w-5 h-5" />
                Филтри
                {activeFilterCount > 0 && (
                  <span className="bg-white text-primary-600 text-xs font-bold px-2 py-0.5 rounded-full">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Mobile Sort */}
          <div className="sm:hidden mt-3">
            <select
              value={`${searchFilters.sort_by}-${searchFilters.sort_order}`}
              onChange={(e) => {
                const [sortBy, sortOrder] = e.target.value.split('-');
                handleSortChange(sortBy, sortOrder as 'asc' | 'desc');
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm"
            >
              <option value="auction_date-desc">Търг (Най-нови)</option>
              <option value="auction_date-asc">Търг (Най-стари)</option>
              <option value="year-desc">Година (Най-нови)</option>
              <option value="year-asc">Година (Най-стари)</option>
              <option value="current_bid-desc">Цена (Най-висока)</option>
              <option value="current_bid-asc">Цена (Най-ниска)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Mobile Filter Drawer */}
      <MobileFilterDrawer 
        isOpen={showMobileFilters} 
        onClose={() => setShowMobileFilters(false)}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={clearFilters}
      >
        <SearchFilters
          filters={searchFilters}
          onFiltersChange={handleFilterChange}
          filterOptions={filterOptions}
        />
      </MobileFilterDrawer>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-6">
          {/* Desktop Filters Sidebar */}
          <div className="hidden lg:block w-80 flex-shrink-0">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 sticky top-24">
              {/* Filter Header */}
              <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center">
                  <FunnelIcon className="w-5 h-5 text-primary-600 mr-2" />
                  <h2 className="text-lg font-bold text-gray-900">Филтри</h2>
                </div>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center"
                  >
                    <XMarkIcon className="w-4 h-4 mr-1" />
                    Изчисти
                  </button>
                )}
              </div>
              
              {/* Filter Content */}
              <div className="p-5 max-h-[calc(100vh-200px)] overflow-y-auto">
                <SearchFilters
                  filters={searchFilters}
                  onFiltersChange={handleFilterChange}
                  filterOptions={filterOptions}
                />
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Active Filters Tags */}
            {hasActiveFilters && (
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <span className="text-sm text-gray-600">Активни филтри:</span>
                {searchFilters.source && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-800">
                    {searchFilters.source === 'IAAI_CANADA' ? '🇨🇦 IAAI Canada' :
                     searchFilters.source === 'IAAI_USA' ? '🇺🇸 IAAI USA' :
                     searchFilters.source === 'COPART' ? '🇺🇸 Copart' : searchFilters.source}
                  </span>
                )}
                {searchFilters.make && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                    {searchFilters.make}
                  </span>
                )}
                {searchFilters.year_from && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
                    От {searchFilters.year_from}
                  </span>
                )}
                {searchFilters.year_to && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
                    До {searchFilters.year_to}
                  </span>
                )}
                {searchFilters.is_buy_now && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-yellow-100 text-yellow-800">
                    🛒 Купи сега
                  </span>
                )}
                <button
                  onClick={clearFilters}
                  className="text-sm text-gray-500 hover:text-red-600 underline"
                >
                  Изчисти всички
                </button>
              </div>
            )}

            {/* Loading State */}
            {isLoading && (
              <div className="flex justify-center py-16">
                <LoadingSpinner size="lg" />
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-200">
                <Cog6ToothIcon className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <p className="text-lg font-medium text-gray-900 mb-2">Грешка при зареждане</p>
                <p className="text-gray-600 mb-4">Моля, опитайте отново.</p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Презареди
                </button>
              </div>
            )}

            {/* Vehicles Grid */}
            {vehiclesData && !isLoading && (
              <>
                {vehiclesData.items.length > 0 ? (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
                      {vehiclesData.items.map((vehicle) => (
                        <VehicleCard key={vehicle.id} vehicle={vehicle} />
                      ))}
                    </div>

                    {/* Pagination */}
                    <div className="mt-8">
                      <Pagination
                        currentPage={vehiclesData.page}
                        totalPages={vehiclesData.total_pages}
                        onPageChange={handlePageChange}
                      />
                    </div>
                  </>
                ) : (
                  <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-200">
                    <TruckIcon className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                    <p className="text-lg font-medium text-gray-900 mb-2">Няма намерени автомобили</p>
                    <p className="text-gray-600 mb-4">Опитайте да промените филтрите.</p>
                    {hasActiveFilters && (
                      <button
                        onClick={clearFilters}
                        className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                      >
                        Изчисти филтрите
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleListingPage;
